import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { base64ToBlob } from '../../../../../common/certificates/certificate-download';
import {
  CERTIFICATE_COLOURS,
  CERTIFICATE_FONTS,
  CERTIFICATE_LAYOUTS,
  CertificateDesign,
  CertificateField,
  CertificateLayout,
  CertificateOrientation,
  CertificateTemplateDraft,
  MERGE_FIELDS,
  loadCertificateFonts,
  toTemplateRequest,
} from './certificate-model';
import { CustomDesignCanvas } from './CustomDesignCanvas';
import { CustomFieldsPanel } from './CustomFieldsPanel';
import { SignaturesEditor } from './SignaturesEditor';

const PRIMARY = '#796FFF';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: { fontSize: '23px', fontWeight: 550, color: theme.color.grey.neutral400, paddingLeft: '5px' },
    content: {
      display: 'flex',
      padding: 0,
      minHeight: '600px',
      [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
    },
    panel: {
      width: '36%',
      minWidth: '340px',
      padding: '20px 24px',
      borderRight: '1px solid #EFEFEF',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: { width: '100%', minWidth: 0, borderRight: 0, borderBottom: '1px solid #EFEFEF' },
    },
    viewer: {
      flex: 1,
      background: '#F9F9F9',
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 24px 24px',
      gap: '12px',
      minHeight: '480px',
    },
    viewerBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '36px' },
    frame: { flex: 1, width: '100%', border: 0, background: '#fff' },
    canvasWrap: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    viewerNote: { fontSize: '13px', color: '#808080', textAlign: 'center' },
    section: { marginBottom: '24px' },
    sectionLabel: { fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400, marginBottom: '8px' },
    helper: { fontSize: '12px', color: '#808080', marginTop: '6px' },
    field: { marginBottom: '16px' },
    tiles: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' },
    option: {
      border: '1px solid #DCDCDC',
      borderRadius: '6px',
      background: '#fff',
      cursor: 'pointer',
      padding: '8px 6px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      color: '#444',
      fontFamily: 'inherit',
      '&:hover': { borderColor: '#9E9E9E' },
    },
    optionSelected: { borderColor: PRIMARY, boxShadow: `0 0 0 1px ${PRIMARY}`, color: PRIMARY },
    thumb: {
      width: '100%',
      height: '34px',
      borderRadius: '2px',
      background: '#F5F5F5',
      position: 'relative',
      overflow: 'hidden',
    },
    swatches: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    swatch: {
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      border: '2px solid #fff',
      boxShadow: '0 0 0 1px #C4C4C4',
      cursor: 'pointer',
      padding: 0,
    },
    swatchSelected: { boxShadow: `0 0 0 2px ${PRIMARY}` },
    switchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
    chip: { fontFamily: 'monospace', fontSize: '12px' },
    error: { color: theme.palette.error.main, fontSize: '13px', marginRight: '8px' },
  })
);

const LAYOUT_THUMBS: Record<CertificateLayout, React.CSSProperties> = {
  classic: { position: 'absolute', inset: '5px', border: '2px solid #BDBDBD' },
  band: { position: 'absolute', left: 0, top: 0, bottom: 0, width: '30%', background: '#BDBDBD' },
  minimal: {
    position: 'absolute',
    left: '50%',
    top: '6px',
    width: '12px',
    height: '12px',
    marginLeft: '-6px',
    borderRadius: '50%',
    background: '#BDBDBD',
  },
  custom: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, #E0E0E0 25%, #F5F5F5 25%, #F5F5F5 50%, #E0E0E0 50%, #E0E0E0 75%, #F5F5F5 75%)',
    backgroundSize: '12px 12px',
  },
};

interface Props {
  open: boolean;
  initialDraft: CertificateTemplateDraft;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: CertificateTemplateDraft) => void;
}

export const CertificateTemplateDialog = ({ open, initialDraft, saving, onClose, onSave }: Props) => {
  const classes = useStyles();
  const { isMobile } = useCampfireTheme();
  const [draft, setDraft] = useState(initialDraft);
  const [error, setError] = useState<string>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [previewing, setPreviewing] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const requestRef = useRef(0);
  const previewFetch = useEndpointFetch<ReturnType<typeof toTemplateRequest>, { pdf: string }>(
    '/vm/admin-console/certificate-template/preview'
  );

  const { design } = draft;
  const custom = design.layout === 'custom';
  const pdfVisible = !custom || showPdf;

  const setDesign = (patch: Partial<CertificateDesign>) =>
    setDraft((current) => ({ ...current, design: { ...current.design, ...patch } }));

  const setField = useCallback(
    (id: string, patch: Partial<CertificateField>) =>
      setDraft((current) => ({
        ...current,
        design: {
          ...current.design,
          fields: current.design.fields.map((field) => (field.id === id ? { ...field, ...patch } : field)),
        },
      })),
    []
  );

  useEffect(() => {
    if (!pdfVisible) return undefined;
    const request = ++requestRef.current;
    setPreviewing(true);
    const timer = setTimeout(() => {
      previewFetch.run(toTemplateRequest(draft)).then((response) => {
        if (request !== requestRef.current) return;
        setPreviewing(false);
        if (!response.ok) return;
        const url = URL.createObjectURL(base64ToBlob(response.data.data.pdf));
        setPreviewUrl((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return url;
        });
      });
    }, 700);
    return () => clearTimeout(timer);
  }, [draft, pdfVisible]);

  useEffect(loadCertificateFonts, []);

  useEffect(
    () => () => {
      requestRef.current += 1;
      setPreviewUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return undefined;
      });
    },
    []
  );

  const insertMergeField = (field: string) => {
    const token = `{{${field}}}`;
    const input = bodyRef.current;
    const start = input?.selectionStart ?? design.body.length;
    const end = input?.selectionEnd ?? design.body.length;
    setDesign({ body: design.body.slice(0, start) + token + design.body.slice(end) });
    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = () => {
    if (!draft.name.trim()) return setError('Give the template a name');
    if (!custom && !design.title.trim()) return setError('Give the certificate a title');
    if (custom && !design.background) return setError('Upload your design');
    setError(undefined);
    return onSave(draft);
  };

  const optionClass = (selected: boolean) => `${classes.option} ${selected ? classes.optionSelected : ''}`;

  const fontSelect = (label: string, value: string, onChange: (font: string) => void) => (
    <FormControl fullWidth variant='outlined' size='small'>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        input={<OutlinedInput notched label={label} />}
      >
        {CERTIFICATE_FONTS.map((font) => (
          <MenuItem key={font.key} value={font.key} style={{ fontFamily: font.css }}>
            {font.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog open={open} onClose={onClose} disableBackdropClick maxWidth='lg' fullWidth fullScreen={isMobile}>
      <DialogTitle disableTypography>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography className={classes.title}>
            {draft.certificateTemplateId ? 'Edit Certificate Template' : 'Add Certificate Template'}
          </Typography>
          <IconButton aria-label='Close' color='inherit' onClick={onClose}>
            <Close color='action' />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        <Box className={classes.panel}>
          <TextField
            fullWidth
            variant='outlined'
            label='Template name'
            value={draft.name}
            onChange={(e) => {
              const name = e.target.value;
              setDraft((current) => ({ ...current, name }));
            }}
            className={classes.field}
          />

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Layout</Typography>
            <Box className={classes.tiles}>
              {(Object.keys(CERTIFICATE_LAYOUTS) as CertificateLayout[]).map((layout) => (
                <button
                  type='button'
                  key={layout}
                  className={optionClass(design.layout === layout)}
                  onClick={() => setDesign({ layout })}
                >
                  <span className={classes.thumb}>
                    <span style={LAYOUT_THUMBS[layout]} />
                  </span>
                  {CERTIFICATE_LAYOUTS[layout]}
                </button>
              ))}
            </Box>
          </Box>

          <FormControl fullWidth variant='outlined' className={classes.field}>
            <InputLabel shrink>Orientation</InputLabel>
            <Select
              value={design.orientation}
              onChange={(e) => setDesign({ orientation: e.target.value as CertificateOrientation })}
              input={<OutlinedInput notched label='Orientation' />}
            >
              <MenuItem value='landscape'>Landscape</MenuItem>
              <MenuItem value='portrait'>Portrait</MenuItem>
            </Select>
          </FormControl>

          {custom ? (
            <>
              <CustomFieldsPanel
                design={design}
                selectedId={selectedFieldId}
                onSelect={setSelectedFieldId}
                onChange={setDesign}
                onChangeField={setField}
              />
              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Signatures</Typography>
                <SignaturesEditor
                  signatories={design.signatories}
                  onChange={(signatories) => setDesign({ signatories })}
                />
                <Typography className={classes.helper}>Add a Signature field to place each signature image.</Typography>
              </Box>
            </>
          ) : (
            <>
              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Colour</Typography>
                <Box className={classes.swatches}>
                  {CERTIFICATE_COLOURS.map((colour) => (
                    <button
                      type='button'
                      key={colour.key}
                      title={colour.name}
                      aria-label={colour.name}
                      className={`${classes.swatch} ${design.colour === colour.key ? classes.swatchSelected : ''}`}
                      style={{ background: colour.hex }}
                      onClick={() => setDesign({ colour: colour.key })}
                    />
                  ))}
                </Box>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Fonts</Typography>
                <Box className={classes.grid}>
                  {fontSelect('Headings', design.headingFont, (headingFont) => setDesign({ headingFont }))}
                  {fontSelect('Body', design.bodyFont, (bodyFont) => setDesign({ bodyFont }))}
                </Box>
              </Box>

              <Box className={classes.section}>
                <Box className={classes.switchRow}>
                  <Typography style={{ fontSize: 14 }}>Show organisation logo</Typography>
                  <Switch
                    color='primary'
                    checked={design.showLogo}
                    onChange={(e) => setDesign({ showLogo: e.target.checked })}
                  />
                </Box>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Wording</Typography>
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Title'
                  value={design.title}
                  onChange={(e) => setDesign({ title: e.target.value })}
                  className={classes.field}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  variant='outlined'
                  label='Body'
                  value={design.body}
                  inputRef={bodyRef}
                  onChange={(e) => setDesign({ body: e.target.value })}
                />
                <Box className={classes.chips}>
                  {MERGE_FIELDS.map((field) => (
                    <Chip
                      key={field}
                      size='small'
                      className={classes.chip}
                      label={`{{${field}}}`}
                      onClick={() => insertMergeField(field)}
                    />
                  ))}
                </Box>
                <Typography className={classes.helper}>
                  A line with only {'{{VOLUNTEER_NAME}}'} prints the name large. {'{{AWARDED_FOR}}'} is the level and
                  achievement, or the reason entered when a certificate is issued by hand.
                </Typography>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionLabel}>Signatures</Typography>
                <SignaturesEditor
                  signatories={design.signatories}
                  onChange={(signatories) => setDesign({ signatories })}
                />
              </Box>
            </>
          )}
        </Box>

        <Box className={classes.viewer}>
          <Box className={classes.viewerBar}>
            {custom ? (
              <Box display='flex' style={{ gap: '8px' }}>
                <TabletButton
                  size='small'
                  variant={showPdf ? 'outlined' : 'contained'}
                  color='primary'
                  onClick={() => setShowPdf(false)}
                >
                  Edit
                </TabletButton>
                <TabletButton
                  size='small'
                  variant={showPdf ? 'contained' : 'outlined'}
                  color='primary'
                  onClick={() => setShowPdf(true)}
                >
                  Preview PDF
                </TabletButton>
              </Box>
            ) : (
              <span />
            )}
          </Box>
          <Box height='4px'>{pdfVisible && previewing && <LinearProgress />}</Box>
          {!pdfVisible ? (
            <Box className={classes.canvasWrap}>
              <Box width='100%' maxWidth={design.orientation === 'landscape' ? '760px' : '480px'}>
                <CustomDesignCanvas
                  design={design}
                  selectedId={selectedFieldId}
                  onSelect={setSelectedFieldId}
                  onChangeField={setField}
                />
              </Box>
            </Box>
          ) : previewUrl ? (
            <iframe title='Certificate preview' className={classes.frame} src={`${previewUrl}#toolbar=0&view=Fit`} />
          ) : (
            <Box flex={1} />
          )}
          <Typography className={classes.viewerNote}>
            {pdfVisible
              ? 'Preview with sample details.'
              : 'Fields show their merge tags. Switch to Preview PDF to see them filled in with sample details.'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {error && <Typography className={classes.error}>{error}</Typography>}
        <TabletButton variant='text' color='error' onClick={onClose} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </TabletButton>
        <TabletButton variant='contained' color='primary' onClick={handleSave} disabled={saving}>
          Save
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
